import { Download, Search } from 'lucide-react';
import { Button, Card, Label, PageHeader, Select } from '@/shared/components';
import { FilterBuilder } from './FilterBuilder';
import { QueryControls } from './QueryControls';
import { ResultsTable } from './ResultsTable';
import { SavedSearchPanel } from './SavedSearchPanel';
import { useAdvancedSearchState } from './useAdvancedSearchState';

export function AdvancedSearchPage() {
  const s = useAdvancedSearchState();
  const datasets = s.catalog.data?.datasets ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Analyse"
        title="Erweiterte"
        titleAccent="Suche"
        description="Filtere einen Datensatz, speichere Vorlagen und exportiere die Ergebnisse."
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={!s.ran}
              onClick={() => void s.exportAs('csv')}
              leftIcon={<Download size={15} strokeWidth={2} />}
            >
              CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!s.ran}
              onClick={() => void s.exportAs('xlsx')}
              leftIcon={<Download size={15} strokeWidth={2} />}
            >
              XLSX
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="min-w-0 flex-1 space-y-4">
          <Card className="space-y-5 p-5">
            <div className="max-w-xs">
              <Label htmlFor="dataset">Datensatz</Label>
              <Select
                id="dataset"
                value={s.dataset}
                onChange={(e) => s.changeDataset(e.target.value)}
              >
                <option value="" disabled>
                  Datensatz wählen …
                </option>
                {datasets.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.key}
                  </option>
                ))}
              </Select>
            </div>

            {s.activeDataset && (
              <>
                <FilterBuilder dataset={s.activeDataset} group={s.group} onChange={s.setGroup} />
                <QueryControls
                  dataset={s.activeDataset}
                  columns={s.columns}
                  sort={s.sort}
                  onColumns={s.setColumns}
                  onSort={s.setSort}
                />
                <div className="border-line/50 flex items-center gap-2 border-t pt-4">
                  <Button
                    variant="brand"
                    leftIcon={<Search size={15} strokeWidth={2} />}
                    loading={s.results.isFetching}
                    onClick={s.run}
                  >
                    Ausführen
                  </Button>
                </div>
              </>
            )}
          </Card>

          {s.results.isError && (
            <p className="text-sealwax text-sm">
              Die Suche ist ungültig oder konnte nicht ausgeführt werden.
            </p>
          )}
          {s.results.data && (
            <ResultsTable
              response={s.results.data}
              onPageChange={s.changePage}
              isFetching={s.results.isFetching}
            />
          )}
        </div>

        <SavedSearchPanel
          items={s.saved.list.data ?? []}
          isLoading={s.saved.list.isLoading}
          onLoad={(id) => void s.loadSaved(id)}
          onDelete={s.removeSaved}
          onSave={s.saveCurrent}
          canSave={Boolean(s.dataset)}
          saving={s.saved.create.isPending}
        />
      </div>
    </div>
  );
}
